import { useContext, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, FormHelperText, Link as MuiLink, OutlinedInput, Stack, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import UserAuthService from '../../../shared/services/user-auth/user-auth.service';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import { AuthLayout } from './auth/auth-layout';
import { useAuthBranding } from './auth/use-auth-branding';
import { AUTH_TOKENS, BRAND_DISPLAY_FONT } from './auth/auth-branding';

const RESEND_COOLDOWN_SECONDS = 30;

export const EmailVerificationPage = () => {
  const userAuthService = new UserAuthService();
  const userDetailsContext = useContext(UserDetailsContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const branding = useAuthBranding();

  const [inProgress, setInProgress] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [sucessMessage, setSucessMessage] = useState<string>('');
  const [cooldown, setCooldown] = useState<number>(0);

  const email = userDetailsContext.userDetails?.email;

  const emailVerificationSchema = Yup.object()
    .shape({
      one: Yup.number().integer().typeError('Your code should consist of numbers only').required(),
      two: Yup.number().integer().typeError('Your code should consist of numbers only').required(),
      three: Yup.number().integer().typeError('Your code should consist of numbers only').required(),
      four: Yup.number().integer().typeError('Your code should consist of numbers only').required(),
      five: Yup.number().integer().typeError('Your code should consist of numbers only').required(),
      six: Yup.number().integer().typeError('Your code should consist of numbers only').required(),
    })
    .test('length', 'Your code must be 6 characters', function (value) {
      const { one, two, three, four, five, six } = value;
      const totalLength = `${one}${two}${three}${four}${five}${six}`.length;
      return totalLength === 6;
    });

  const formik = useFormik({
    initialValues: { one: '', two: '', three: '', four: '', five: '', six: '' },
    validationSchema: emailVerificationSchema,
    onSubmit: (values) => {
      const formattedAuthenticationCode = `${values.one}${values.two}${values.three}${values.four}${values.five}${values.six}`;
      userAuthService
        .verifyEmailUser({ verificationCode: formattedAuthenticationCode })
        .then(() => {
          userDetailsContext.forceUpdateUserDetails().then(() => {
            if (userDetailsContext.userDetails?.organisation?.name) {
              navigate('/dashboard', { state: { justAuthenticated: true } });
            } else {
              navigate('/complete-registration');
            }
          });
        })
        .catch((err) => {
          console.log(err);
          setInProgress(false);
          setErrorMessage(err.response.data.message);
        });
    },
  });

  const refOne = useRef<HTMLInputElement>(null);
  const refTwo = useRef<HTMLInputElement>(null);
  const refThree = useRef<HTMLInputElement>(null);
  const refFour = useRef<HTMLInputElement>(null);
  const refFive = useRef<HTMLInputElement>(null);
  const refSix = useRef<HTMLInputElement>(null);
  const refs = [refOne, refTwo, refThree, refFour, refFive, refSix];

  // Prefill from a ?verificationCode deep link.
  useEffect(() => {
    if (searchParams.has('verificationCode')) {
      const token = searchParams.get('verificationCode');
      if (token) {
        searchParams.delete('verificationCode');
        setSearchParams(searchParams);
        formik.setValues({
          one: token[0],
          two: token[1],
          three: token[2],
          four: token[3],
          five: token[4],
          six: token[5],
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Paste a full code into any box.
  useEffect(() => {
    const handlePaste = (e: Event) => {
      if (!refOne.current) return;
      e.preventDefault();
      const clipboardEvent = e as ClipboardEvent;
      const chars = (clipboardEvent.clipboardData?.getData('text') ?? '').split('');
      const [one, two, three, four, five, six] = chars;
      formik.setValues({
        one: one ?? '',
        two: two ?? '',
        three: three ?? '',
        four: four ?? '',
        five: five ?? '',
        six: six ?? '',
      });
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refOne.current?.focus();
  }, []);

  // Auto-submit once all six digits are present.
  useEffect(() => {
    const { one, two, three, four, five, six } = formik.values;
    const totalLength = `${one}${two}${three}${four}${five}${six}`.length;
    if (totalLength === 6 && !inProgress) {
      setInProgress(true);
      formik.submitForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values]);

  // Resend cooldown tick.
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const authenticationCodeHandler = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    const { name, value } = e.target as HTMLInputElement;
    formik.setValues({ ...formik.values, [name]: value.toUpperCase() });
    if (value.length === 0) {
      setInProgress(false);
    }
    if (value.length === 1 && index < refs.length - 1) {
      refs[index + 1].current?.focus();
    }
  };

  // Backspace on an empty box steps focus back.
  const keyDownHandler = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    if (e.key === 'Backspace' && !(e.target as HTMLInputElement).value && index > 0) {
      refs[index - 1].current?.focus();
    }
  };

  const resendHandler = async () => {
    if (cooldown > 0) return;
    setErrorMessage('');
    userAuthService
      .verifyCodeUser()
      .then((response) => {
        setSucessMessage(response.message);
        setCooldown(RESEND_COOLDOWN_SECONDS);
      })
      .catch((err) => console.log(err));
  };

  const hasNumbersOnlyError = Object.values(formik.errors).some((error) => (error as string).includes('numbers only'));

  const codeInputSx = {
    width: { xs: 44, sm: 52 },
    '& .MuiOutlinedInput-root': {
      height: 56,
      borderRadius: '11px',
      backgroundColor: 'common.white',
      '& input': { textAlign: 'center', fontSize: '1.35rem', fontWeight: 600, p: 0 },
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: AUTH_TOKENS.border },
    '& .MuiOutlinedInput-root.Mui-focused': {
      boxShadow: `0 0 0 3px ${branding.focusRing}`,
      '& .MuiOutlinedInput-notchedOutline': { borderWidth: '1px', borderColor: 'primary.main' },
    },
  } as const;

  return (
    <AuthLayout branding={branding}>
      <Typography
        component="h1"
        sx={{ mb: '6px', fontSize: '1.6rem', fontFamily: BRAND_DISPLAY_FONT, letterSpacing: '-0.015em', color: 'text.primary' }}
      >
        Verify your email
      </Typography>
      <Typography sx={{ mb: '26px', fontSize: '0.9375rem', lineHeight: 1.5, color: AUTH_TOKENS.inkSecondary }}>
        Enter the 6-digit code we sent to {email ? <b>{email}</b> : 'your email address'}.
      </Typography>

      <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: '16px' }}>
        {(Object.keys(formik.values) as (keyof typeof formik.values)[]).map((key, index) => (
          <OutlinedInput
            key={key}
            id={key}
            name={key}
            inputRef={refs[index]}
            value={formik.values[key]}
            onChange={(e) => authenticationCodeHandler(e, index)}
            onKeyDown={(e) => keyDownHandler(e, index)}
            placeholder="·"
            inputProps={{ maxLength: 1, inputMode: 'numeric', 'aria-label': `Digit ${index + 1}` }}
            sx={codeInputSx}
          />
        ))}
      </Stack>

      {(hasNumbersOnlyError || errorMessage) && (
        <FormHelperText error role="alert" sx={{ ml: '2px', mb: 1, fontSize: '0.8125rem' }}>
          {errorMessage || 'Your code should consist of numbers only'}
        </FormHelperText>
      )}

      {inProgress && !errorMessage && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, color: AUTH_TOKENS.inkSecondary }}>
          <CircularProgress size={16} color="inherit" />
          <Typography sx={{ fontSize: '0.8125rem' }}>Verifying…</Typography>
        </Stack>
      )}

      <Typography sx={{ mt: '8px', fontSize: '0.875rem', color: AUTH_TOKENS.inkSecondary }}>
        Didn&apos;t get it?{' '}
        <MuiLink
          component="button"
          type="button"
          onClick={resendHandler}
          disabled={cooldown > 0}
          underline="none"
          sx={{
            fontWeight: 500,
            color: cooldown > 0 ? AUTH_TOKENS.inkTertiary : 'primary.main',
            cursor: cooldown > 0 ? 'default' : 'pointer',
            verticalAlign: 'baseline',
          }}
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
        </MuiLink>
      </Typography>

      {sucessMessage && (
        <FormHelperText role="status" sx={{ mt: 1, ml: '2px', color: 'primary.main', fontSize: '0.8125rem' }}>
          {sucessMessage}
        </FormHelperText>
      )}
    </AuthLayout>
  );
};
